// 语音输入（FR-3.2）。优先微信原生录音 + translateVoice（微信自带 ASR，免服务端）；
// 非微信环境回退浏览器 Web Speech API；都不支持则禁用按钮。长按录音、松开转写。
import type { Script } from "./types";
import { getWx, inWeChat, wxReady } from "./wechat";

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((e: unknown) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};

interface SpeechCtor {
  new (): SpeechRecognitionLike;
}

function speechCtor(): SpeechCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechCtor;
    webkitSpeechRecognition?: SpeechCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function voiceSupported(): boolean {
  return inWeChat() || !!speechCtor();
}

export interface VoiceController {
  start(): void;
  stop(): void; // 正常结束并转写
  cancel(): void; // 取消（上滑）
}

export interface VoiceHooks {
  onResult: (text: string) => void;
  onError: (msg: string) => void;
  getScript: () => Script;
}

export function createVoice(hooks: VoiceHooks): VoiceController {
  // ---------- 微信路径 ----------
  if (inWeChat()) {
    let recording = false;
    let canceled = false;
    return {
      start() {
        canceled = false;
        wxReady().then((ok) => {
          const wx = getWx();
          if (!ok || !wx?.startRecord) {
            hooks.onError("voice_unsupported");
            return;
          }
          recording = true;
          wx.startRecord?.();
        });
      },
      stop() {
        const wx = getWx();
        if (!recording || !wx?.stopRecord) return;
        recording = false;
        wx.stopRecord({
          success: (res) => {
            if (canceled) return;
            wx.translateVoice?.({
              localId: res.localId,
              isShowProgressTips: 1,
              success: (r) => hooks.onResult((r.translateResult || "").replace(/。$/, "")),
              fail: () => hooks.onError("voice_unsupported"),
            });
          },
        });
      },
      cancel() {
        canceled = true;
        recording = false;
        getWx()?.stopRecord?.({ success: () => void 0 });
      },
    };
  }

  // ---------- Web Speech API 路径 ----------
  const Ctor = speechCtor();
  let rec: SpeechRecognitionLike | null = null;
  let canceled = false;

  return {
    start() {
      if (!Ctor) {
        hooks.onError("voice_unsupported");
        return;
      }
      canceled = false;
      rec = new Ctor();
      rec.lang = hooks.getScript() === "zh-Hans" ? "zh-CN" : "zh-TW";
      rec.continuous = false;
      rec.interimResults = false;
      rec.onresult = (e) => {
        if (canceled) return;
        const text = e.results?.[0]?.[0]?.transcript ?? "";
        if (text) hooks.onResult(text);
      };
      rec.onerror = () => hooks.onError("voice_unsupported");
      rec.onend = () => {
        rec = null;
      };
      try {
        rec.start();
      } catch {
        hooks.onError("voice_unsupported");
      }
    },
    stop() {
      rec?.stop();
    },
    cancel() {
      canceled = true;
      rec?.abort();
      rec = null;
    },
  };
}
